import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputLabel,
  OutlinedInput,
  Stack,
  SvgIcon,
  Typography,
  FormControl,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  styled,
} from '@mui/material';
import { changeNameFormat } from 'utils/Utils';

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow: 'inset 0 0 0 1.5px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 20,
    height: 20,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

/**
 * A MUI Checkbox with a custom checked icon and styles.
 *
 * This component is used in the AddTechnology dialog to allow users to select
 * technologies to add to their profile.
 *
 * @param {object} props
 * @param {boolean} props.checked Whether the checkbox is checked or not.
 * @param {function} props.onChange A function to call when the checkbox is
 *                                  changed.
 * @param {string} props.name The name of the checkbox.
 * @param {object} props Any other props to forward to the underlying MUI
 *                        Checkbox component.
 * @returns {ReactElement} The custom checkbox component.
 */
function BpCheckbox({ checked, onChange, name, ...props }) {
  return (
    <Checkbox
      sx={{ '&:hover': { bgcolor: 'transparent' }, padding: { xs: "0px", sm: "0px", md: "9px" } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      checked={checked}
      onChange={onChange}
      name={name}
      {...props}
    />
  );
}
/**
 * AddTechnology component
 *
 * This component renders a dialog that allows users to search for and select
 * technologies to add to their profile. It displays a list of available
 * technologies and provides a search bar to filter the list. Users can select
 * up to 12 technologies, and an error message is shown if the limit is exceeded.
 * 
 * @param {Function} onClose - Function to close the dialog.
 * @param {Array} technology - Array of available technologies to display and select.
 * @param {Array} selectedsoftware - Array of initially selected technologies.
 * @param {Function} onAddTechnology - Function to handle the addition of selected technologies.
 * 
 * @returns {ReactElement} The rendered AddTechnology dialog component.
 */

const AddTechnology = ({ onClose, technology, selectedsoftware, onAddTechnology }) => {
  const [selectedSoftware, setSelectedSoftware] = useState(selectedsoftware || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  const focusRef = useRef(null);
  const [filteredTechnologies, setFilteredTechnologies] = useState(Array.from(new Set([...selectedsoftware, ...technology])));


  useLayoutEffect(() => {
    focusRef.current?.focus();
  }, [])

  /**
   * Handles the done button click event. If the selected software length exceeds 12, 
   * an error message is set. Otherwise, the selected technologies are passed to the 
   * onAddTechnology function and the dialog is closed.
   */
  const handleDoneClick = () => {
    if (selectedSoftware.length > 12) {
      setError('You cannot add more than 12 competencies');
    } else {
      onAddTechnology(selectedSoftware);
      onClose();
    }
  };

  /**
   * Handles the checkbox change event for each technology in the dialog.
   * If the checkbox is checked, the technology is added to the selectedSoftware
   * array if it is not already there. If the checkbox is unchecked, the technology
   * is removed from the selectedSoftware array. If the selectedSoftware array
   * length exceeds 12, an error message is set.
   * @param {object} event - The checkbox change event.
   */
  const handleCheckboxChange = (event) => {
    const technology = event.target.name;
    if (event.target.checked) {
      if (selectedSoftware.length >= 12) {
        setError('You cannot add more than 12 competencies');
        return;
      }
      setSelectedSoftware((prevSelectedSkills) => [...prevSelectedSkills, technology]);
    } else {
      setSelectedSoftware((prevSelectedSkills) => prevSelectedSkills.filter((skill) => skill !== technology));
      setError('');
    }
  };

  /**
   * Handles the search bar change event for the AddTechnology dialog. If the search value
   * is not empty and does not match any of the available technologies, the search value is
   * converted to title case and added to the filtered technologies array. Otherwise, the
   * filtered technologies array is updated to include only technologies that match the
   * search value (case-insensitive).
   * @param {object} event - The change event.
   */
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    let filterTech = [];
    const match = technology.filter((skill) => skill.toLowerCase().includes(value.toLowerCase()));

    if (value && value !== '' && match?.length === 0) {
      filterTech = [changeNameFormat(value)];
      filterTech = filterTech.filter((skill) => skill.toLowerCase().includes(value.toLowerCase()));
    } else {
      filterTech = technology.filter((skill) => skill.toLowerCase().includes(value.toLowerCase()));
    }
    setFilteredTechnologies(filterTech);
  };

  return (
    <Card className="add-skills-dialog" variant="outlined">
      <CardContent>
        <IconButton sx={{ display: { xs: "none", sm: "none" } }} className="close-dialog-btn" aria-label="delete" color="primary" onClick={onClose}>
          <SvgIcon sx={{ width: '20px', height: '20px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_456_20993)">
                <path
                  d="M7 7L17 17M17 7L7 17L17 7Z"
                  stroke="#111827"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_456_20993">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </SvgIcon>
        </IconButton>

        <Box mb={2.5}>
          <Typography className="title xs-fs-16" component="h6" >
            Add technical competencies
          </Typography>
          <Typography className="sub-text xs-fs-12  " component="p">
            Please select from list below
          </Typography>
        </Box>

        {error && (
          <Box mb={1.5}>
            <Typography className="error-text" component="p" style={{ color: 'red' }}>
              {error}
            </Typography>
          </Box>
        )}

        <Box mb={1.5}>
          <FormControl fullWidth>
            <TextField
              label="Competency name"
              inputRef={focusRef}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
            />
          </FormControl>
        </Box>

        <Box className="add-skills-dialog-content" mb={3.5}>
          <Box className="content-inner" sx={{ paddingLeft: { xs: 0, md: 2 } }}>
            <FormGroup>
              {filteredTechnologies
                .map((label, index) => (
                  <FormControlLabel
                    control={
                      <BpCheckbox
                        checked={selectedSoftware.includes(label)}
                        onChange={handleCheckboxChange}
                        name={label}
                      />
                    }
                    key={index}
                    label={label}
                    sx={{ alignItems: "center !important" }}
                  />
                ))}
            </FormGroup>

          </Box>
        </Box>

        <Box className="skills-dialog-actions">
          <Stack direction="row" justifyContent="center" alignItems={'center'} spacing={{ xs: 2, md: 3 }}>
            <Button fullWidth className="otp-btn" variant="outlined" size="medium" onClick={onClose} tabIndex={-1}>
              Cancel
            </Button>
            <Button fullWidth className="otp-btn" variant="contained" size="medium" onClick={handleDoneClick}>
              Done
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AddTechnology;
